import type { Table as TanStackTable } from '@tanstack/react-table';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { TableCell, TableHead, TableRow } from '../../../../design-system/table/table';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { cn } from '../../../../utils/cn';
import { ReportingBalances } from '../../utils/get-reporting-balances';
import { ReportingData } from '../../utils/get-reporting-data';

const MONTH_AMOUNT_COLUMN_IDS = [
  'three_months_ago_amount',
  'two_months_ago_amount',
  'previous_month_amount',
  'current_month_amount',
] as const;

type ReportingBalanceRowProps = {
  table: TanStackTable<ReportingData>;
  reportingBalances: ReportingBalances;
  baseCurrency: SupportedCountryCurrency;
  tableWidth: number;
  getMonthColumnClasses: (columnId: string, isFirstColumn: boolean) => Record<string, boolean>;
  type: 'start' | 'end';
};

export const ReportingBalanceRow = ({
  table,
  reportingBalances,
  baseCurrency,
  tableWidth,
  getMonthColumnClasses,
  type,
}: ReportingBalanceRowProps) => {
  const label = type === 'start' ? 'Cash at the Start' : 'Cash at the End';
  const rowKey = type === 'start' ? 'start-balance' : 'end-balance';
  const CellComponent = type === 'start' ? TableHead : TableCell;
  const rowClassName = type === 'start' ? 'bg-muted hover:bg-muted' : 'border-t-0 bg-muted hover:bg-muted';
  const cellPadding = type === 'start' ? 'px-2 py-4' : 'px-2 py-3';
  const cellAlignment = type === 'start' ? 'align-bottom' : '';

  const getBalanceValue = (columnId: string): number => {
    switch (columnId) {
      case 'three_months_ago_amount':
        return type === 'start' ? reportingBalances.month3.start : reportingBalances.month3.end;
      case 'two_months_ago_amount':
        return type === 'start' ? reportingBalances.month2.start : reportingBalances.month2.end;
      case 'previous_month_amount':
        return type === 'start' ? reportingBalances.month1.start : reportingBalances.month1.end;
      case 'current_month_amount':
        return type === 'start' ? reportingBalances.month0.start : reportingBalances.month0.end;
      default:
        return 0;
    }
  };

  const headerGroups = type === 'start' ? table.getHeaderGroups() : table.getFooterGroups();

  return (
    <>
      {headerGroups.map((group) => (
        <TableRow key={`${rowKey}-${group.id}`} className={rowClassName}>
          {group.headers.map((header) => {
            const isFirstColumn = header.column.getIsFirstColumn();
            const isLastColumn = header.column.getIsLastColumn();
            const isMonthAmountColumn = MONTH_AMOUNT_COLUMN_IDS.includes(
              header.column.id as (typeof MONTH_AMOUNT_COLUMN_IDS)[number],
            );

            const sumSize = table.getTotalSize();
            const cellSize = header.column.getSize();
            const cellWidthPercentage = cellSize / sumSize;
            const finalWidth = cellWidthPercentage * tableWidth;

            let content: React.ReactNode = null;
            if (isFirstColumn) {
              content = type === 'start' ? <span className="text-sm font-semibold text-black">{label}</span> : label;
            } else if (isMonthAmountColumn) {
              const value = getBalanceValue(header.column.id);
              content = (
                <AmountDisplay
                  normalizedAmount={value}
                  currency={baseCurrency}
                  isAccountBalance={true}
                  className={type === 'start' ? 'font-semibold text-black' : 'text-black'}
                />
              );
            }

            return (
              <CellComponent
                key={`${rowKey}-${header.id}`}
                className={cn(cellPadding, cellAlignment, {
                  'pl-5 text-start': isFirstColumn,
                  'px-0': header.column.id === 'actions',
                  'pr-0': isMonthAmountColumn,
                  'pr-6': isLastColumn,
                  'font-semibold': isFirstColumn || isMonthAmountColumn || isLastColumn,
                  ...getMonthColumnClasses(header.column.id, isFirstColumn),
                })}
                style={{
                  width: `${finalWidth}px`,
                }}
              >
                {content}
              </CellComponent>
            );
          })}
        </TableRow>
      ))}
    </>
  );
};
